import { Popup } from "@/components/Popup";
import { InfoIcon } from "@/designSystemIcons";

interface LearnMorePopupProps {
  onClose: () => void;
}

export const LearnMorePopup = ({ onClose }: LearnMorePopupProps) => {
  return (
    <Popup
      title="How it works"
      onClose={onClose}
      titleIcon={
        <InfoIcon innerFill="white" outerFill="var(--color-primary-blue)" />
      }
      absolute
    >
      <div className="flex flex-col gap-y-8 py-6 px-4 text-dark-tertiary text-xs mobile:text-sm ">
        <section className="flex flex-col gap-y-4 items-center">
          <img
            src={
              import.meta.env.VITE_APP_ASSETS_BASE_URL +
              "/fc25/my-club/learn-more-popup/base-recommendation.png"
            }
            alt="My Recommendations"
            className="w-50 h-31 mx-auto"
          />
          <p className="text-center max-w-124">
            <span className="text-dark-secondary">My Recommendations</span>{" "}
            finds the best upgrades for your Active Squad when you make changes
            to it or when new content comes out.
          </p>
        </section>
        <div className="flex flex-col touch-device:flex-row gap-4 h-full">
          <section className="flex flex-col touch-device:min-w-68 flex-1 gap-y-1">
            <p className="text-white mb-1">Possible upgrades</p>
            <div className="p-3 bg-popup-inner-box rounded-lg flex flex-col touch-device:flex-wrap gap-4 flex-1">
              <ImageWithText
                title="Squad Meta Rating"
                urlSuffix="meta-rating-for-squad.png"
                type="upgrade-types"
              />
              <ImageWithText
                title="Chemistry for Squad"
                urlSuffix="chemistry-for-squad.png"
                type="upgrade-types"
              />
              <ImageWithText
                title="Meta Rating for Position"
                urlSuffix="meta-rating-for-position.png"
                type="upgrade-types"
              />
              <ImageWithText
                title="Chemistry for Position"
                urlSuffix="chemistry-for-position.png"
                type="upgrade-types"
              />
              <p className="italic text-xs">
                Recommendations are sorted by Squad Meta Rating.
              </p>
            </div>
          </section>
          <section className="flex flex-col touch-device:min-w-68 flex-1 gap-y-1">
            <p className="text-white mb-1">
              Where to obtain recommended players
            </p>
            <div className="p-3 bg-popup-inner-box rounded-lg flex flex-col touch-device:flex-wrap gap-4 flex-1">
              <ImageWithText
                title="SBCs"
                urlSuffix="sbc.png"
                type="recommendation-types"
              />
              <ImageWithText
                title="Your Club"
                urlSuffix="club.png"
                type="recommendation-types"
              />
              <ImageWithText
                title="Evolutions"
                urlSuffix="evolution.png"
                type="recommendation-types"
              />
              <ImageWithText
                title="Transfer Market"
                urlSuffix="transfer.png"
                type="recommendation-types"
              />
              <ImageWithText
                title="Objectives & Season Points"
                urlSuffix="objective.png"
                type="recommendation-types"
              />
            </div>
          </section>
          <section className="flex flex-col touch-device:min-w-68 flex-1 gap-y-1">
            <p className="text-dark-secondary mb-1">
              Effort Score{" "}
              <span className="text-dark-tertiary">
                (Evolutions and Objectives)
              </span>
            </p>
            <div className="p-3 bg-popup-inner-box rounded-lg flex flex-col touch-device:flex-wrap gap-4 text-xs text-dark-tertiary flex-1">
              <div className="flex flex-col gap-y-2">
                <div className="bg-panel-foreground rounded-lg w-fit flex flex-row gap-x-1 items-center justify-center font-medium h-6 px-2">
                  <span className="text-dark-secondary">Low Effort</span>
                  <div className="w-2 h-2 text-white rounded-full bg-complete" />
                </div>
                <span className="max-w-63">
                  Play a few games in any game mode.
                </span>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="bg-panel-foreground rounded-lg w-fit flex flex-row gap-x-1 items-center justify-center font-medium h-6 px-2">
                  <span className="text-dark-secondary">Medium Effort</span>
                  <div className="w-2 h-2 text-white rounded-full bg-[#FDB022]" />
                </div>
                <span className="max-w-63">
                  Play a few games in Friendlies/Champs, several in Rivals/Rush,
                  or many in Squad Battles.
                </span>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="bg-panel-foreground rounded-lg w-fit flex flex-row gap-x-1 items-center justify-center font-medium h-6 px-2">
                  <span className="text-dark-secondary">High Effort</span>
                  <div className="w-2 h-2 text-white rounded-full bg-error" />
                </div>
                <span className="max-w-63">
                  Play several games in Friendlies/Champs, or many in
                  Rivals/Rush.
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Popup>
  );
};

interface UpgradeTypeProps {
  title: string;
  urlSuffix: string;
  type: "upgrade-types" | "recommendation-types";
}

export const ImageWithText: React.FC<UpgradeTypeProps> = ({
  title,
  urlSuffix,
  type,
}) => {
  return (
    <div className="flex flex-row gap-x-3 items-center">
      <img
        className="w-8 h-8"
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/my-club/learn-more-popup/${type}/${urlSuffix}`}
        alt={title}
      />
      <p>{title}</p>
    </div>
  );
};
